import React, { useState, useEffect } from "react";
import {
  Shield,
  Users,
  Heart,
  BookOpen,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Briefcase,
  Activity,
  Target,
  Home,
} from "lucide-react";

const RakaiT1DProject = () => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
  }, []);

  const projectStats = [
    { number: "250-300", label: "People with T1D Supported", icon: Users },
    { number: "36", label: "Months Duration", icon: Activity },
    { number: "5", label: "Thematic Areas", icon: Target },
    { number: "Rakai", label: "District Focus", icon: Home },
  ];

  const programPillars = [
    {
      icon: BookOpen,
      title: "Advocacy & Policy Influence",
      description:
        "Generate evidence and advocacy momentum to elevate T1D within district and national NCD priorities through patient-led platforms and policy engagement.",
      highlights: [
        "Rakai T1D Patient & Caregiver Advocacy Platform",
        "Policy dialogues with District Health Office & MOH",
        "Development of policy briefs using patient data",
        "Media engagement amplifying lived experience",
        "Participation in national NCD advocacy spaces",
      ],
      outcome: "T1D explicitly reflected in district health plans and budgets",
    },
    {
      icon: DollarSign,
      title: "Financial Protection & Livelihood Resilience",
      description:
        "Reduce financial and social inequities experienced by people living with T1D through emergency support and economic empowerment initiatives.",
      highlights: [
        "Emergency health cost protection fund",
        "Conditional cash/voucher support linked to clinic attendance",
        "Livelihood skills development for youth and adults",
        "T1D-inclusive savings and lending groups (VSLAs)",
      ],
      outcome:
        "Reduced catastrophic health expenditure and improved household stability",
    },
    {
      icon: Briefcase,
      title: "Health Systems Strengthening",
      description:
        "Strengthen public health systems to deliver continuous, quality T1D care through capacity building and infrastructure support.",
      highlights: [
        "Training health workers on T1D management",
        "Facility-based T1D patient registries",
        "Strengthened referral and follow-up pathways",
        "Essential diagnostic equipment aligned with MOH standards",
      ],
      outcome:
        "Public health facilities equipped to manage T1D as routine care",
    },
    {
      icon: Activity,
      title: "Treatment & Continuity of Care",
      description:
        "Improve access to essential T1D treatment and monitoring through system-aligned support and community outreach.",
      highlights: [
        "Gap-filling support for insulin, glucometers, and test strips",
        "Community outreach clinics for hard-to-reach populations",
        "Individualized care plans and adherence support",
        "Nutrition and self-management education",
      ],
      outcome:
        "Improved adherence, glycemic monitoring, and reduced emergency complications",
    },
    {
      icon: Heart,
      title: "Wellbeing, Psychosocial Support & Leadership",
      description:
        "Strengthen psychosocial wellbeing, agency, and leadership among people living with T1D through peer support and community inclusion.",
      highlights: [
        "Peer support groups segmented by age and caregiver status",
        "Psychosocial counseling integrated into follow-up",
        "School engagement for children with T1D",
        "Annual leadership and wellbeing camps for youth",
      ],
      outcome:
        "Improved mental wellbeing, self-efficacy, and community inclusion",
    },
  ];

  const problemStatements = [
    {
      title: "Under-prioritized",
      description: "T1D remains under-prioritized within national NCD responses",
    },
    {
      title: "Poorly Financed",
      description:
        "Weak financing at household and health system levels creates barriers",
    },
    {
      title: "Fragmented Support",
      description: "Dependent on fragmented donor support without continuity",
    },
    {
      title: "Preventable Deaths",
      description:
        "Associated with preventable morbidity, mortality, and social exclusion",
    },
  ];

  const systemicInequities = [
    "Unreliable insulin and diagnostics supply",
    "Limited provider capacity at primary health care level",
    "Absence of patient registries and continuity of care",
    "Financial catastrophe and loss of productivity",
    "Weak psychosocial and community support structures",
  ];

  const whyItMatters = [
    {
      title: "Equity-Centered NCD Care",
      description:
        "Demonstrates how equity-centered NCD care can be delivered through public systems",
    },
    {
      title: "Evidence to Policy",
      description:
        "Links lived experience → data → advocacy → policy change",
    },
    {
      title: "Global Learning",
      description:
        "Generates practical learning for global T1D and NCD strategies",
    },
    {
      title: "Scalable Blueprint",
      description:
        "Creates a scalable blueprint adaptable to other districts and countries",
    },
  ];

  return (
    <div className="project-page">
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Urgent Banner */}
      <section
        className="urgent-banner"
        role="note"
        aria-label="T1D urgent care callout"
      >
        <div className="container">
          <div className="urgent-content">
            <span className="urgent-icon">
              <AlertTriangle size={20} />
            </span>
            <p className="urgent-text">
              <strong>Type 1 Diabetes is life-threatening but manageable.</strong>{" "}
              In rural Uganda, children die from preventable causes. Help us
              change this reality.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container position-relative">
          <div className="row align-items-center min-vh-80">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge">Demonstration Model</div>
                <h1 className="hero-title">Rakai T1D Project</h1>
                <p className="hero-subtitle">
                  An Integrated, Equity-Driven Model for Type 1 Diabetes Care
                </p>
                <p className="hero-description">
                  Building an equity-centered, systems-integrated model for Type
                  1 Diabetes care that improves survival, wellbeing, and
                  socio-economic resilience in rural Uganda. A demonstration
                  project designed to inform district, national, and global T1D
                  strategies.
                </p>
                <div className="hero-stats-grid">
                  {projectStats.map((stat, index) => (
                    <div key={index} className="stat-card">
                      <stat.icon size={24} className="stat-icon" />
                      <div className="stat-number">{stat.number}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-container">
                <div className="project-image">
                  <img
                    src="/images/projects/t1d-hero.jpeg"
                    alt="Rakai T1D Project"
                    className="hero-image"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="image-placeholder">
                    <Heart size={60} />
                    <span>Type 1 Diabetes Care</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="problem-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div
                className={`section-header ${animateIn ? "animate-in" : ""}`}
              >
                <div className="section-badge">The Challenge</div>
                <h2 className="section-title">
                  A Condition of Profound Inequity
                </h2>
                <p className="section-description">
                  Type 1 Diabetes is life-threatening but highly treatable. Yet
                  in rural Uganda, it remains under-prioritized, poorly
                  financed, and dependent on fragmented support—leading to
                  preventable deaths among children and young adults.
                </p>
              </div>

              <div className="problem-grid">
                {problemStatements.map((problem, index) => (
                  <div key={index} className="problem-card">
                    <h4 className="problem-title">{problem.title}</h4>
                    <p className="problem-description">{problem.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-lg-6">
              <div className="inequities-card">
                <h3 className="card-title">
                  Systemic Inequities in Rakai District
                </h3>
                <p className="card-subtitle">
                  People living with T1D face multiple barriers to care:
                </p>
                <ul className="inequities-list">
                  {systemicInequities.map((inequity, index) => (
                    <li key={index}>
                      <AlertTriangle size={18} />
                      <span>{inequity}</span>
                    </li>
                  ))}
                </ul>
                <div className="call-to-action-box">
                  <p>
                    <strong>
                      Without intentional systems strengthening and advocacy,
                      T1D will continue to be a silent cause of preventable
                      deaths.
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Goal Section */}
      <section className="goal-section">
        <div className="container">
          <div className="goal-card">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <div className="section-badge mb-3">Project Goal</div>
                <h2 className="goal-title">
                  Demonstrate and Scale an Equity-Centered Model
                </h2>
                <p className="goal-description">
                  To demonstrate and scale an equity-centered, systems-integrated
                  model for Type 1 Diabetes care that improves survival,
                  wellbeing, and socio-economic resilience in rural Uganda.
                </p>
              </div>
              <div className="col-lg-4">
                <div className="goal-icon-container">
                  <Target size={80} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Thematic Areas / Program Pillars */}
      <section className="pillars-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div
                className={`section-header text-center ${
                  animateIn ? "animate-in" : ""
                }`}
              >
                <div className="section-badge">Thematic Areas</div>
                <h2 className="section-title">
                  Five Pillars of Integrated Care
                </h2>
                <p className="section-description">
                  A comprehensive approach linking advocacy, financial
                  protection, health systems, treatment access, and psychosocial
                  wellbeing to transform T1D care in rural settings.
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            {programPillars.map((pillar, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <div className="pillar-card">
                  <div className="pillar-icon">
                    <pillar.icon size={32} />
                  </div>
                  <div className="pillar-content">
                    <h3 className="pillar-title">{pillar.title}</h3>
                    <p className="pillar-description">{pillar.description}</p>
                    <div className="pillar-section">
                      <h5 className="pillar-section-title">Key Activities</h5>
                      <ul className="pillar-highlights">
                        {pillar.highlights.map((highlight, idx) => (
                          <li key={idx}>
                            <TrendingUp size={16} />
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="expected-outcome">
                      <strong>Expected Outcome:</strong> {pillar.outcome}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="why-matters-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div
                className={`section-header text-center ${
                  animateIn ? "animate-in" : ""
                }`}
              >
                <div className="section-badge">Impact & Scale</div>
                <h2 className="section-title">Why This Project Matters</h2>
                <p className="section-description">
                  Beyond direct impact, this project is designed to generate
                  learning and influence policy at multiple levels
                </p>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {whyItMatters.map((reason, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <div className="matters-card">
                  <div className="matters-icon">
                    <Shield size={28} />
                  </div>
                  <h4 className="matters-title">{reason.title}</h4>
                  <p className="matters-description">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="scalability-section">
            <div className="row">
              <div className="col-12">
                <div className="scalability-card">
                  <h3 className="scalability-title">Designed for Scale</h3>
                  <div className="row">
                    <div className="col-md-3">
                      <div className="scalability-item">
                        <BookOpen size={24} />
                        <span>Standardized training modules and tools</span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="scalability-item">
                        <DollarSign size={24} />
                        <span>Costed implementation model</span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="scalability-item">
                        <Users size={24} />
                        <span>District co-ownership</span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="scalability-item">
                        <Target size={24} />
                        <span>National and global dissemination</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Partners */}
      <section className="partners-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="partners-card">
                <h3 className="partners-title">
                  Implementation & Learning Partner
                </h3>
                <div className="organization-info">
                  <div className="org-logo">
                    <Home size={48} />
                  </div>
                  <div className="org-details">
                    <h4>Rakai Community Health Initiative (RACOHI)</h4>
                    <p className="org-description">
                      A grassroots nonprofit community-based Ugandan organization
                      working to improve health outcomes among underserved
                      populations in Rakai District.
                    </p>
                    <div className="org-experience">
                      <h5>Key Experience Areas:</h5>
                      <ul>
                        <li>Community-led health programming</li>
                        <li>Health systems engagement at district level</li>
                        <li>Advocacy and stakeholder coordination</li>
                        <li>Integrated health and livelihood interventions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monitoring & Learning */}
      <section className="monitoring-section">
        <div className="container">
          <div className="monitoring-card">
            <div className="row align-items-center">
              <div className="col-lg-3">
                <div className="monitoring-icon">
                  <Activity size={60} />
                </div>
              </div>
              <div className="col-lg-9">
                <h3 className="monitoring-title">
                  Monitoring, Learning & Influence
                </h3>
                <div className="monitoring-items">
                  <div className="monitoring-item">
                    <TrendingUp size={20} />
                    <span>Baseline, midline, endline assessments</span>
                  </div>
                  <div className="monitoring-item">
                    <TrendingUp size={20} />
                    <span>Patient registry and service utilization data</span>
                  </div>
                  <div className="monitoring-item">
                    <TrendingUp size={20} />
                    <span>Equity and financial protection indicators</span>
                  </div>
                  <div className="monitoring-item">
                    <TrendingUp size={20} />
                    <span>
                      Annual learning briefs for policymakers and partners
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="cta-title">
                Support the Rakai T1D Project
              </h2>
              <p className="cta-description">
                Help us demonstrate that equity-centered Type 1 Diabetes care is
                possible in rural Uganda. Your support will directly improve
                survival rates, reduce family financial burden, and inform
                national policy.
              </p>
              <div className="cta-stats">
                <div className="cta-stat">
                  <span className="stat-number">250-300</span>
                  <span className="stat-label">People with T1D to be supported</span>
                </div>
                <div className="cta-stat">
                  <span className="stat-number">36</span>
                  <span className="stat-label">Months to demonstrate impact</span>
                </div>
                <div className="cta-stat">
                  <span className="stat-number">National</span>
                  <span className="stat-label">Policy influence potential</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles */}
      <style jsx>{`
        :root {
          --primary-color: #8b5cf6;
          --primary-dark: #7c3aed;
          --primary-light: #a78bfa;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --text-light: #9ca3af;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
          --success-green: #10b981;
          --warning-red: #ef4444;
        }

        /* Urgent Banner */
        .urgent-banner {
          background: linear-gradient(135deg, #7c3aed, #8b5cf6);
          color: #fff;
          padding: 12px 0;
        }

        .urgent-content {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: center;
          text-align: center;
        }

        .urgent-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 999px;
          padding: 6px;
        }

        .urgent-text {
          margin: 0;
          font-size: 0.98rem;
          line-height: 1.4;
          font-weight: 500;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
            sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          min-height: 85vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 60px 0 40px;
        }
        .hero-background {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
          z-index: 0;
        }
        .hero-overlay {
          position: absolute;
          inset: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(139,92,246,0.05)"/><circle cx="80" cy="80" r="1.5" fill="rgba(139,92,246,0.03)"/></svg>')
            repeat;
          z-index: 0;
        }

        .hero-content {
          position: relative;
          z-index: 2;
        }
        .hero-badge {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(139, 92, 246, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4.5rem);
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 16px;
          line-height: 1.1;
        }
        .hero-subtitle {
          font-size: 1.5rem;
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 20px;
          line-height: 1.3;
        }
        .hero-description {
          font-size: 1.15rem;
          color: var(--text-gray);
          margin-bottom: 32px;
          line-height: 1.7;
          max-width: 600px;
        }

        .hero-stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-top: 24px;
        }

        .stat-card {
          background: white;
          padding: 16px;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: transform 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
        }

        .stat-icon {
          color: var(--primary-color);
          margin-bottom: 6px;
        }

        .stat-number {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 4px;
        }

        .stat-label {
          display: block;
          font-size: 0.85rem;
          color: var(--text-gray);
        }

        .hero-image-container {
          position: relative;
          z-index: 2;
        }
        .project-image {
          position: relative;
          width: 100%;
          height: 450px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(139, 92, 246, 0.25);
        }
        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .image-placeholder {
          display: none;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          color: white;
          text-align: center;
          gap: 16px;
        }

        /* Problem Section */
        .problem-section {
          padding: 60px 0;
          background: white;
        }

        .problem-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-top: 24px;
        }

        .problem-card {
          background: var(--light-bg);
          padding: 20px;
          border-radius: 12px;
          border-left: 4px solid var(--warning-red);
        }

        .problem-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 8px;
        }

        .problem-description {
          font-size: 0.95rem;
          color: var(--text-gray);
          margin: 0;
        }

        .inequities-card {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
          height: 100%;
        }

        .card-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .card-subtitle {
          color: var(--text-gray);
          margin-bottom: 20px;
        }

        .inequities-list {
          list-style: none;
          padding: 0;
          margin: 0 0 24px 0;
        }

        .inequities-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        .inequities-list li svg {
          color: var(--warning-red);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .call-to-action-box {
          background: linear-gradient(135deg, #7c3aed, #8b5cf6);
          padding: 20px;
          border-radius: 12px;
          color: white;
        }

        .call-to-action-box p {
          margin: 0;
          font-size: 1.05rem;
          line-height: 1.6;
        }

        /* Goal Section */
        .goal-section {
          padding: 60px 0;
          background: var(--light-bg);
        }

        .goal-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
        }

        .goal-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .goal-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          line-height: 1.7;
          margin: 0;
        }

        .goal-icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 140px;
          height: 140px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          color: white;
          margin: 0 auto;
        }

        /* Pillars Section */
        .pillars-section {
          padding: 60px 0;
          background: white;
        }
        .section-header {
          margin-bottom: 48px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }
        .section-header.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .section-badge {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(139, 92, 246, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 16px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .section-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .section-description {
          font-size: 1.1rem;
          color: var(--text-gray);
          margin-bottom: 24px;
          line-height: 1.6;
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
        }
        .pillar-card {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          border: 1px solid var(--border-light);
        }
        .pillar-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(139, 92, 246, 0.15);
        }
        .pillar-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 20px;
        }
        .pillar-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 12px;
        }
        .pillar-description {
          color: var(--text-gray);
          margin-bottom: 20px;
          line-height: 1.6;
        }
        .pillar-section {
          margin-bottom: 16px;
        }
        .pillar-section-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 10px;
        }
        .pillar-highlights {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .pillar-highlights li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 8px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }
        .pillar-highlights li svg {
          color: var(--success-green);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .expected-outcome {
          background: rgba(139, 92, 246, 0.08);
          padding: 14px;
          border-radius: 8px;
          font-size: 0.95rem;
          color: var(--text-gray);
          margin-top: 16px;
        }
        .expected-outcome strong {
          color: var(--primary-color);
        }

        /* Why It Matters Section */
        .why-matters-section {
          padding: 60px 0;
          background: var(--light-bg);
        }

        .matters-card {
          background: white;
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          height: 100%;
          text-align: center;
        }

        .matters-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 24px rgba(139, 92, 246, 0.15);
        }

        .matters-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin: 0 auto 16px;
        }

        .matters-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 10px;
        }

        .matters-description {
          font-size: 0.95rem;
          color: var(--text-gray);
          margin: 0;
          line-height: 1.6;
        }

        .scalability-section {
          margin-top: 48px;
        }

        .scalability-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
        }

        .scalability-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 32px;
          text-align: center;
        }

        .scalability-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 12px;
          padding: 16px;
        }

        .scalability-item svg {
          color: var(--primary-color);
        }

        .scalability-item span {
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        /* Partners Section */
        .partners-section {
          padding: 60px 0;
          background: white;
        }

        .partners-card {
          background: var(--light-bg);
          border-radius: 20px;
          padding: 48px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        }

        .partners-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 32px;
          text-align: center;
        }

        .organization-info {
          display: flex;
          gap: 28px;
          align-items: flex-start;
          background: white;
          padding: 32px;
          border-radius: 16px;
        }

        .org-logo {
          width: 100px;
          height: 100px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .org-details h4 {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .org-description {
          color: var(--text-gray);
          margin-bottom: 20px;
          line-height: 1.6;
        }

        .org-experience h5 {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 10px;
        }

        .org-experience ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .org-experience li {
          padding-left: 24px;
          position: relative;
          margin-bottom: 6px;
          color: var(--text-gray);
        }

        .org-experience li:before {
          content: "•";
          position: absolute;
          left: 8px;
          color: var(--primary-color);
          font-weight: bold;
        }

        /* Monitoring Section */
        .monitoring-section {
          padding: 60px 0;
          background: var(--light-bg);
        }

        .monitoring-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
        }

        .monitoring-icon {
          width: 120px;
          height: 120px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin: 0 auto;
        }

        .monitoring-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 28px;
        }

        .monitoring-items {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .monitoring-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: var(--light-bg);
          border-radius: 8px;
        }

        .monitoring-item svg {
          color: var(--success-green);
          flex-shrink: 0;
        }

        .monitoring-item span {
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        /* CTA Section */
        .cta-section {
          padding: 60px 0;
          background: white;
        }

        .cta-card {
          background: linear-gradient(135deg, #7c3aed, #8b5cf6);
          border-radius: 24px;
          padding: 60px 48px;
          color: white;
          text-align: center;
        }

        .cta-content {
          max-width: 900px;
          margin: 0 auto;
        }

        .cta-title {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 20px;
          line-height: 1.2;
        }

        .cta-description {
          font-size: 1.2rem;
          margin-bottom: 40px;
          opacity: 0.95;
          line-height: 1.7;
        }

        .cta-stats {
          display: flex;
          justify-content: center;
          gap: 40px;
          flex-wrap: wrap;
        }

        .cta-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .cta-stat .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 6px;
        }

        .cta-stat .stat-label {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .hero-content,
          .hero-image-container {
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }
        @media (max-width: 992px) {
          .hero-content {
            padding-right: 0;
            margin-bottom: 32px;
          }
          .hero-image-container {
            padding-left: 0;
          }
          .hero-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .problem-grid {
            grid-template-columns: 1fr;
          }
          .organization-info {
            flex-direction: column;
            text-align: center;
          }
          .org-logo {
            margin: 0 auto;
          }
          .monitoring-items {
            grid-template-columns: 1fr;
          }
          .scalability-item {
            margin-bottom: 16px;
          }
        }
        @media (max-width: 768px) {
          .hero-section {
            padding: 40px 0 20px;
          }
          .hero-stats-grid {
            grid-template-columns: 1fr;
          }
          .pillar-card {
            padding: 24px 20px;
          }
          .cta-card {
            padding: 48px 24px;
          }
          .cta-title {
            font-size: 2rem;
          }
          .goal-card,
          .partners-card,
          .monitoring-card {
            padding: 32px 20px;
          }
          .cta-stats {
            gap: 28px;
          }
          .problem-section,
          .goal-section,
          .pillars-section,
          .why-matters-section,
          .partners-section,
          .monitoring-section,
          .cta-section {
            padding: 40px 0;
          }
        }

        /* Animation */
        .animate-in {
          animation: slideInUp 0.8s ease-out forwards;
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default RakaiT1DProject;